// src/services/PreAuthLogService.js
// Logs d'avant connexion (login, inscription, vérification email, crashs au chargement) : la
// collection `appLogs` exige un utilisateur connecté, ces erreurs y étaient donc invisibles.
// Collection `preAuthLogs` : écriture anonyme mais BORNÉE par firestore.rules (liste blanche de
// types et de champs, tailles maximales, timestamp serveur). Aucune donnée personnelle :
// jamais d'email, de mot de passe ni d'uid — uniquement code d'erreur, étape et appareil.
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getSessionId } from '../utils/sessionId';

const IS_PROD = process.env.NODE_ENV === 'production';
const MAX_PER_SESSION = 20;
let sent = 0;
const seen = new Set();

// Champs acceptés par les règles — toute autre clé ferait refuser le document.
const ALLOWED_FIELDS = [
  'step', 'code', 'errorMessage', 'stack', 'page', 'chunkLoad', 'line', 'col', 'tag', 'url',
  'ua', 'platform', 'uaMobile', 'touchPoints', 'standalone', 'lang', 'build',
  'innerW', 'innerH', 'screenW', 'screenH', 'dpr', 'orientation', 'vvScale',
];

export function preAuthLog(level, type, params = {}) {
  if (!IS_PROD) return;
  try {
    const key = `${type}|${params.step || ''}|${params.code || ''}|${String(params.errorMessage || '').slice(0, 60)}|${params.page || ''}`;
    if (sent >= MAX_PER_SESSION || seen.has(key)) return;
    seen.add(key);
    sent += 1;

    const doc = { level, type, sessionId: getSessionId(), ua: navigator.userAgent.substring(0, 120) };
    for (const f of ALLOWED_FIELDS) {
      if (params[f] !== undefined && params[f] !== null) doc[f] = typeof params[f] === 'string' ? params[f].slice(0, 500) : params[f];
    }
    doc.timestamp = serverTimestamp();
    addDoc(collection(db, 'preAuthLogs'), doc).catch(() => { /* silencieux */ });
  } catch { /* le monitoring ne casse jamais l'app */ }
}

// Codes d'erreur Firebase Auth imputables à l'utilisateur (saisie) : information, pas alerte.
const USER_FAULT = new Set([
  'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-credential', 'auth/invalid-email',
  'auth/email-already-in-use', 'auth/weak-password', 'auth/missing-password', 'auth/user-disabled',
  'auth/expired-action-code', 'auth/invalid-action-code', 'auth/email-not-verified',
]);

/** À appeler dans les catch d'authentification. step : 'login' | 'signup' | 'reset_send' | ... */
export function logAuthFailure(step, error, extra = {}) {
  const code = String(error?.code || 'unknown').slice(0, 80);
  preAuthLog(USER_FAULT.has(code) ? 'info' : 'warning', 'auth_failure', {
    step,
    code,
    errorMessage: code.startsWith('auth/') ? '' : String(error?.message || '').replace(/\S+@\S+\.\S+/g, '<email>').slice(0, 200),
    page: window.location.pathname,
    ...extra,
  });
}
